import { useCallback, useEffect, useState } from "react";
import { api, formatDateTime } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Chip } from "@/components/Chip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Facebook, Linkedin, Send } from "lucide-react";
import { toast } from "sonner";

const CHANNELS = {
  whatsapp: { label: "WhatsApp", icon: MessageSquare, color: "text-emerald-600 bg-emerald-50" },
  facebook: { label: "Facebook", icon: Facebook, color: "text-blue-600 bg-blue-50" },
  linkedin: { label: "LinkedIn", icon: Linkedin, color: "text-sky-700 bg-sky-50" },
};

export default function Inbox() {
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");

  const loadConvos = useCallback(() => api.get("/inbox/conversations").then(r => {
    setConvos(r.data);
    setActive(prev => prev || (r.data.length ? r.data[0] : null));
  }), []);
  useEffect(() => { loadConvos(); }, [loadConvos]);

  useEffect(() => {
    if (!active) return;
    api.get(`/inbox/conversations/${active.id}/messages`).then(r => setMsgs(r.data));
  }, [active]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const r = await api.post(`/inbox/conversations/${active.id}/reply`, { text });
    setMsgs(prev => [...prev, r.data]);
    setText("");
    loadConvos();
    toast.success("Reply sent (mock)");
  };

  return (
    <div className="space-y-6 h-full flex flex-col">
      <PageHeader
        title="Omnichannel Inbox"
        subtitle="WhatsApp · Facebook · LinkedIn — all customer conversations in one place"
      />

      <div className="text-xs bg-amber-50 text-amber-800 border border-amber-200 px-3 py-2 rounded-md">
        <strong>Note:</strong> Live channel sync (Meta / LinkedIn) is <strong>MOCKED</strong> in this build.
        Connect real APIs later — the UI is production-ready.
      </div>

      <div className="flex-1 bg-white border border-slate-200 rounded-lg overflow-hidden flex min-h-[600px]">
        <aside className="w-80 border-r border-slate-200 flex flex-col">
          <div className="p-3 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-medium">
            Inbox ({convos.length})
          </div>
          <div className="flex-1 overflow-y-auto">
            {convos.map(c => {
              const ch = CHANNELS[c.channel] || CHANNELS.whatsapp;
              return (
                <button key={c.id} onClick={() => setActive(c)}
                        className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 flex gap-3 items-start ${active?.id === c.id ? "bg-brand-aqua-light" : ""}`}
                        data-testid={`convo-${c.id}`}>
                  <div className={`w-8 h-8 rounded-md ${ch.color} flex items-center justify-center shrink-0`}>
                    <ch.icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <div className="text-sm font-medium text-slate-800 truncate">{c.contact_name}</div>
                      {c.unread > 0 && <Chip variant="aqua">{c.unread}</Chip>}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{c.last_message_preview}</div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">{ch.label}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <div className="flex-1 flex flex-col">
          {active ? (
            <>
              <div className="border-b border-slate-200 px-4 py-3">
                <div className="font-heading font-semibold text-slate-900">{active.contact_name}</div>
                <div className="text-xs text-slate-500 capitalize">via {active.channel}</div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/30">
                {msgs.map(m => (
                  <div key={m.id} className={`max-w-[70%] rounded-lg px-3 py-2 text-sm ${m.direction === "inbound" ? "bg-white border border-slate-200 text-slate-800 mr-auto" : "bg-brand-aqua text-white ml-auto"}`}>
                    <div>{m.text}</div>
                    <div className={`text-[10px] mt-1 ${m.direction === "inbound" ? "text-slate-400" : "text-white/70"}`}>{formatDateTime(m.created_at)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-200 p-3 flex gap-2">
                <Input placeholder="Type your reply..." value={text} onChange={e => setText(e.target.value)}
                       onKeyDown={e => e.key === "Enter" && send()} data-testid="inbox-reply-input" />
                <Button onClick={send} disabled={!text.trim()} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="inbox-send-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">Select a conversation</div>
          )}
        </div>
      </div>
    </div>
  );
}
